<template>
  <div class="ledger-tab">
    <section class="panel">
      <div class="panel-header">
        <div>
          <h3>Partner ledger</h3>
          <p>Commission rows for one partner (latest 100), in USD. Expand a row to see every adjustment.</p>
        </div>
      </div>
      <el-form :inline="true" size="small" @submit.native.prevent="loadLedger">
        <el-form-item label="Partner ID">
          <el-input v-model.trim="affiliateInput" maxlength="24" clearable placeholder="24-character user ID" style="width: 260px" class="mono-input" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" native-type="submit" icon="el-icon-search" :loading="ledgerLoading">Load ledger</el-button>
        </el-form-item>
      </el-form>

      <template v-if="ledgerAffiliate">
        <div class="summary">
          <div class="summary-item">
            <div class="summary-label">Rows</div>
            <div class="summary-value">{{ ledger.length }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Commission</div>
            <div class="summary-value">{{ formatUsdCents(totals.current) }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Paid</div>
            <div class="summary-value">{{ formatUsdCents(totals.paid) }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">In payout</div>
            <div class="summary-value">{{ formatUsdCents(totals.reserved) }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Unpaid balance</div>
            <div class="summary-value" :class="{ negative: totals.owing < 0 }">{{ formatUsdCents(totals.owing) }}</div>
          </div>
        </div>

        <el-table v-loading="ledgerLoading" :data="ledger" border size="small" style="width: 100%" row-key="_id" empty-text="No ledger rows for this partner">
          <el-table-column type="expand">
            <template slot-scope="scope">
              <div class="expand-body">
                <el-table :data="scope.row.entries || []" size="mini" border empty-text="No entries">
                  <el-table-column label="Version" width="80">
                    <template slot-scope="entry">{{ entry.row.version || entry.$index + 1 }}</template>
                  </el-table-column>
                  <el-table-column label="Recorded" width="160">
                    <template slot-scope="entry">{{ formatTime(entry.row.created_at, true) }}</template>
                  </el-table-column>
                  <el-table-column label="Change" width="120">
                    <template slot-scope="entry">
                      <span :class="{ negative: Number(entry.row.delta_cents) < 0 }">{{ formatUsdCents(entry.row.delta_cents) }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="New commission" width="140">
                    <template slot-scope="entry">{{ formatUsdCents(entry.row.target_cents) }}</template>
                  </el-table-column>
                  <el-table-column label="Net receipt" width="130">
                    <template slot-scope="entry">{{ formatUsdCents(entry.row.net_cents) }}</template>
                  </el-table-column>
                  <el-table-column label="Rate" width="90">
                    <template slot-scope="entry">{{ formatBps(entry.row.rate_bps) }}</template>
                  </el-table-column>
                </el-table>
                <div class="muted expand-meta">
                  Order <span class="mono">{{ scope.row.order_id || '-' }}</span>
                  · Stripe account {{ scope.row.stripe_account || '-' }}
                  <span v-if="scope.row.settled_batch_ids && scope.row.settled_batch_ids.length">
                    · Settled in <span class="mono">{{ scope.row.settled_batch_ids.join(', ') }}</span>
                  </span>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Payment key" min-width="170">
            <template slot-scope="scope">
              <span class="mono" :title="idOf(scope.row)">{{ shortId(scope.row, 18) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Type" width="130">
            <template slot-scope="scope">
              <div>{{ orderTypeLabel(scope.row.order_type) }}</div>
              <el-tag v-if="scope.row.livemode === false" size="mini" type="warning">Test</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Commission" width="110">
            <template slot-scope="scope">{{ formatUsdCents(scope.row.current_cents) }}</template>
          </el-table-column>
          <el-table-column label="Paid" width="100">
            <template slot-scope="scope">{{ formatUsdCents(scope.row.paid_cents || 0) }}</template>
          </el-table-column>
          <el-table-column label="In payout" width="150">
            <template slot-scope="scope">
              <div>{{ scope.row.reserved_cents ? formatUsdCents(scope.row.reserved_cents) : '-' }}</div>
              <div v-if="scope.row.reserved_batch" class="muted mono" :title="scope.row.reserved_batch">{{ shortId(scope.row.reserved_batch, 12) }}</div>
            </template>
          </el-table-column>
          <el-table-column label="Hold until" width="150">
            <template slot-scope="scope">
              <div>{{ formatTime(scope.row.hold_until) }}</div>
              <el-tag v-if="Number(scope.row.hold_until || 0) * 1000 > now" size="mini" type="info">On hold</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="Created" width="150">
            <template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template>
          </el-table-column>
          <el-table-column label="Adj." width="60">
            <template slot-scope="scope">{{ Math.max(0, (scope.row.entries || []).length - 1) }}</template>
          </el-table-column>
        </el-table>
      </template>
    </section>

    <section class="panel">
      <div class="panel-header">
        <div>
          <h3>Exception queues</h3>
          <p>Operator review queues, oldest first. Payments, webhooks, and trial discounts are retried by the worker; resolve the cause and let it replay.</p>
        </div>
        <div class="header-actions">
          <el-select v-model="exceptionLimit" size="small" style="width: 120px" @change="loadExceptions">
            <el-option v-for="limit in [25, 50, 100]" :key="limit" :label="`${limit} per queue`" :value="limit" />
          </el-select>
          <el-button size="small" icon="el-icon-refresh" :loading="exceptionsLoading" @click="loadExceptions">Refresh</el-button>
        </div>
      </div>

      <div v-loading="exceptionsLoading">
        <div v-if="exceptions && !exceptionTotal" class="empty">All exception queues are empty.</div>
        <el-collapse v-if="exceptions" v-model="openQueues">
          <el-collapse-item v-for="queue in queues" :key="queue.key" :name="queue.key">
            <template slot="title">
              <span class="queue-title">{{ queue.title }}</span>
              <el-tag size="mini" :type="queueRows(queue).length ? 'warning' : 'info'" class="queue-count">{{ queueRows(queue).length }}</el-tag>
            </template>
            <div class="muted queue-help">{{ queue.help }}</div>
            <el-table v-if="queueRows(queue).length" :data="queueRows(queue)" border size="mini" style="width: 100%">
              <el-table-column type="expand">
                <template slot-scope="scope">
                  <pre class="raw-json">{{ prettyJson(scope.row) }}</pre>
                </template>
              </el-table-column>
              <el-table-column v-for="column in queue.columns" :key="column.label" :label="column.label" :min-width="column.width || 130">
                <template slot-scope="scope">
                  <el-tag v-if="column.type === 'tag' && cellValue(scope.row, column)" size="mini" type="warning">{{ cellValue(scope.row, column) }}</el-tag>
                  <span v-else-if="column.type === 'id'" class="mono" :title="cellValue(scope.row, column)">{{ shortId(cellValue(scope.row, column), 16) || '-' }}</span>
                  <span v-else-if="column.type === 'time'">{{ formatTime(cellValue(scope.row, column)) }}</span>
                  <span v-else-if="column.type === 'usd'">{{ formatUsdCents(cellValue(scope.row, column)) }}</span>
                  <span v-else class="cell-text">{{ displayValue(cellValue(scope.row, column)) }}</span>
                </template>
              </el-table-column>
              <el-table-column v-if="queue.action" label="Actions" width="130" fixed="right">
                <template slot-scope="scope">
                  <el-button
                    v-if="queue.action === 'recover'"
                    size="mini"
                    type="primary"
                    plain
                    :loading="isPending(`recover:${idOf(scope.row)}`)"
                    :disabled="anyPending"
                    @click="recoverCheckout(queue, scope.row)"
                  >
                    Recover
                  </el-button>
                  <el-button
                    v-if="queue.action === 'reconcile' && canReconcile(scope.row)"
                    size="mini"
                    type="primary"
                    plain
                    :loading="isPending(`reconcile:${scope.row.batch_id}:${scope.row.payment_key}`)"
                    :disabled="anyPending"
                    @click="reconcileItem(scope.row)"
                  >
                    Reconcile
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-collapse-item>
        </el-collapse>
      </div>
    </section>
  </div>
</template>

<script>
import {
  listAffiliateExceptions,
  listAffiliateLedger,
  reconcileAffiliatePayoutItem,
  recoverExpiredWelcomeCheckout
} from '@/apis/lfAdmin'
import affiliateMixin, { OBJECT_ID_PATTERN } from './affiliateMixin'

const WELCOME_COLUMNS = [
  { label: 'Quote', prop: '_id', type: 'id' },
  { label: 'Order', prop: 'order_id', type: 'id' },
  { label: 'Status', prop: 'status', type: 'tag' },
  { label: 'Checkout session', prop: 'stripe_session_id', type: 'id' },
  { label: 'Created', prop: 'created_at', type: 'time' },
  { label: 'Recovery', prop: 'recovery_status' },
  { label: 'Recovery error', prop: 'recovery_error', width: 200 }
]

// The backend returns `welcome_order_checkouts` / `welcome_subscription_checkouts`;
// the contract document names them `welcome_orders` / `welcome_subscriptions`. Accept both.
const QUEUES = [
  {
    key: 'payments',
    title: 'Payments',
    help: 'Commission payments held or waiting for facts: fees, replays, claims, disputes, unsupported invoices, or paused earning.',
    columns: [
      { label: 'Payment key', prop: '_id', type: 'id' },
      { label: 'Status', prop: 'status', type: 'tag' },
      { label: 'Order type', prop: 'order_type' },
      { label: 'Partner', prop: 'affiliate_id', type: 'id' },
      { label: 'Updated', prop: 'updated_at', type: 'time' },
      { label: 'Error', prop: ['allocation_error', 'last_error', 'error'], width: 200 }
    ]
  },
  {
    key: 'webhooks',
    title: 'Stripe webhook retries',
    help: 'Events that failed processing and are waiting for the next retry.',
    columns: [
      { label: 'Event', prop: 'event_id', type: 'id' },
      { label: 'Type', prop: 'type', width: 180 },
      { label: 'Attempts', prop: 'attempts', width: 80 },
      { label: 'Received', prop: 'received_at', type: 'time' },
      { label: 'Next attempt', prop: 'next_attempt_at', type: 'time' },
      { label: 'Last error', prop: 'last_error', width: 220 }
    ]
  },
  {
    key: 'payout_items',
    title: 'Payout items',
    help: 'Connect transfers with an unknown or unfinished outcome. Reconcile checks Stripe for items still sending or with an unknown outcome; confirmed items finish when the batch dispatch is resumed from the Payouts tab.',
    action: 'reconcile',
    columns: [
      { label: 'Batch', prop: 'batch_id', type: 'id' },
      { label: 'Payment key', prop: 'payment_key', type: 'id' },
      { label: 'Status', prop: 'status', type: 'tag' },
      { label: 'Transfer', prop: 'transfer_id', type: 'id' },
      { label: 'Created', prop: 'created_at', type: 'time' },
      { label: 'Last error', prop: 'last_error', width: 200 }
    ]
  },
  {
    key: 'payout_batches',
    title: 'Payout batches in progress',
    help: 'Batches left dispatching or recording. Resume them from the Payouts tab.',
    columns: [
      { label: 'Batch', prop: '_id', type: 'id' },
      { label: 'Partner', prop: 'affiliate_id', type: 'id' },
      { label: 'Status', prop: 'status', type: 'tag' },
      { label: 'Method', prop: 'payout_method' },
      { label: 'Total (USD)', prop: 'total_cents', type: 'usd' },
      { label: 'Created', prop: 'created_at', type: 'time' }
    ]
  },
  {
    key: 'trial_discounts',
    title: 'Trial subscription discounts',
    help: 'Subscriptions whose partner discount could not be applied to the first paid invoice.',
    columns: [
      { label: 'Subscription', prop: '_id', type: 'id' },
      { label: 'User', prop: 'user_id', type: 'id' },
      { label: 'Status', prop: 'affiliate_discount_status', type: 'tag' },
      { label: 'Error', prop: 'affiliate_discount_error', width: 220 },
      { label: 'Checked', prop: 'affiliate_discount_checked_at', type: 'time' }
    ]
  },
  {
    key: 'welcome_order_checkouts',
    altKey: 'welcome_orders',
    title: 'Welcome-offer order checkouts',
    help: 'One-time purchase quotes whose Checkout creation stalled. Recover releases the buyer\'s welcome offer and cap reservation once Stripe confirms the Checkout expired unpaid.',
    action: 'recover',
    kind: 'one_time',
    columns: WELCOME_COLUMNS
  },
  {
    key: 'welcome_subscription_checkouts',
    altKey: 'welcome_subscriptions',
    title: 'Welcome-offer subscription checkouts',
    help: 'Subscription quotes whose Checkout creation stalled. Recover releases the buyer\'s welcome offer and cap reservation once Stripe confirms the Checkout expired unpaid.',
    action: 'recover',
    kind: 'subscription',
    columns: WELCOME_COLUMNS
  }
]

export default {
  name: 'AffiliateLedgerTab',
  mixins: [affiliateMixin],
  props: {
    affiliateId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      affiliateInput: '',
      ledgerAffiliate: '',
      ledgerLoading: false,
      ledger: [],
      now: Date.now(),
      queues: QUEUES,
      exceptionLimit: 50,
      exceptionsLoading: false,
      exceptions: null,
      openQueues: []
    }
  },
  computed: {
    totals() {
      return this.ledger.reduce((sum, row) => {
        const current = Number(row.current_cents || 0)
        const paid = Number(row.paid_cents || 0)
        const reserved = Number(row.reserved_cents || 0)
        sum.current += current
        sum.paid += paid
        sum.reserved += reserved
        sum.owing += current - paid - reserved
        return sum
      }, { current: 0, paid: 0, reserved: 0, owing: 0 })
    },
    exceptionTotal() {
      return this.queues.reduce((sum, queue) => sum + this.queueRows(queue).length, 0)
    }
  },
  watch: {
    affiliateId: {
      immediate: true,
      handler(id) {
        if (id && OBJECT_ID_PATTERN.test(id) && id !== this.ledgerAffiliate) {
          this.affiliateInput = id
          this.loadLedger()
        }
      }
    }
  },
  mounted() {
    this.loadExceptions()
  },
  methods: {
    orderTypeLabel(type) {
      const map = { collection: 'Collection', bundle: 'Bundle', platform_subscription: 'Subscription' }
      return map[type] || type || '-'
    },
    async loadLedger() {
      const id = this.affiliateInput
      if (!OBJECT_ID_PATTERN.test(id)) {
        this.$message.warning('Partner ID must be a 24-character hex ID.')
        return
      }
      this.ledgerLoading = true
      const data = await this.loadData(() => listAffiliateLedger({ affiliate_id: id }), 'Failed to load ledger')
      if (data !== undefined) {
        this.ledger = Array.isArray(data) ? data : []
        this.ledgerAffiliate = id
        this.now = Date.now()
        this.$emit('affiliate-change', id)
      }
      this.ledgerLoading = false
    },
    canReconcile(row) {
      return !!(row.batch_id && row.payment_key) && ['dispatching', 'unknown'].includes(row.status)
    },
    queueRows(queue) {
      if (!this.exceptions) return []
      const rows = this.exceptions[queue.key] || (queue.altKey && this.exceptions[queue.altKey]) || []
      return Array.isArray(rows) ? rows : []
    },
    async loadExceptions() {
      this.exceptionsLoading = true
      const data = await this.loadData(() => listAffiliateExceptions({ limit: this.exceptionLimit }), 'Failed to load exception queues')
      if (data !== undefined) {
        this.exceptions = data || {}
        this.openQueues = this.queues.filter((queue) => this.queueRows(queue).length).map((queue) => queue.key)
      }
      this.exceptionsLoading = false
    },
    cellValue(row, column) {
      const props = Array.isArray(column.prop) ? column.prop : [column.prop]
      for (const prop of props) {
        const value = prop === '_id' ? this.idOf(row) : row[prop]
        if (value !== null && value !== undefined && value !== '') return value
      }
      return ''
    },
    displayValue(value) {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'boolean') return value ? 'Yes' : 'No'
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },
    prettyJson(row) {
      try {
        return JSON.stringify(row, null, 2)
      } catch (error) {
        return String(row)
      }
    },
    async recoverCheckout(queue, row) {
      const quoteId = this.idOf(row)
      const savedSession = row.stripe_session_id || ''
      const lines = [
        `Recover the ${queue.kind === 'one_time' ? 'one-time purchase' : 'subscription'} welcome-offer quote ${quoteId}?`,
        'The backend asks Stripe for the Checkout session. Only a session confirmed expired and unpaid releases the buyer\'s welcome offer and any offer-cap reservation; anything else is left untouched.'
      ]
      let sessionId = ''
      if (savedSession) {
        lines.push(`Saved Checkout session: ${savedSession}`)
        const confirmed = await this.confirmAction({ title: 'Recover expired checkout', lines, confirmText: 'Recover' })
        if (!confirmed) return
      } else {
        lines.push('No Checkout session is saved on this quote. If you found it in the Stripe Dashboard, enter it below; otherwise leave it empty.')
        try {
          const { value } = await this.$prompt(this.confirmMessage(lines), 'Recover expired checkout', {
            confirmButtonText: 'Recover',
            cancelButtonText: 'Cancel',
            type: 'warning',
            inputPlaceholder: 'Optional: cs_live_… or cs_test_…',
            closeOnClickModal: false,
            inputValidator: (input) => {
              const text = String(input || '').trim()
              if (!text || /^cs_[A-Za-z0-9_]+$/.test(text)) return true
              return 'Enter a Stripe Checkout session ID that starts with cs_.'
            }
          })
          sessionId = String(value || '').trim()
        } catch (error) {
          return
        }
      }
      const params = { kind: queue.kind, quote_id: quoteId }
      if (sessionId) params.stripe_session_id = sessionId
      const result = await this.runMutation(`recover:${quoteId}`, () => recoverExpiredWelcomeCheckout(params), {
        success: 'Expired unpaid checkout recovered',
        failure: 'Failed to recover checkout'
      })
      if (result) this.loadExceptions()
    },
    async reconcileItem(row) {
      const batchId = row.batch_id
      const paymentKey = row.payment_key
      const confirmed = await this.confirmAction({
        title: 'Reconcile payout item',
        lines: [
          `Check Stripe for the transfer of payment ${paymentKey} in batch ${batchId}?`,
          'A matching transfer is recorded as paid. If Stripe has no transfer 15 minutes after the attempt, the item is marked absent and the batch can be dispatched again.'
        ],
        confirmText: 'Reconcile',
        type: 'info'
      })
      if (!confirmed) return
      const result = await this.runMutation(`reconcile:${batchId}:${paymentKey}`, () => reconcileAffiliatePayoutItem({ batch_id: batchId, payment_key: paymentKey }), {
        failure: 'Failed to reconcile payout item'
      })
      if (result) {
        const transfer = result.data && result.data.transfer_id
        this.$message.success(transfer ? `Transfer ${transfer} reconciled` : 'No transfer exists; the item can be dispatched again')
        this.loadExceptions()
      }
    }
  }
}
</script>

<style scoped>
.panel {
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #ebeef5;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.panel-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.panel-header p {
  margin: 4px 0 0;
  color: #909399;
  font-size: 13px;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1px;
  margin-bottom: 12px;
  background: #ebeef5;
  border: 1px solid #ebeef5;
}

.summary-item {
  flex: 1 1 140px;
  padding: 10px 14px;
  background: #fff;
}

.summary-label {
  color: #909399;
  font-size: 12px;
}

.summary-value {
  margin-top: 2px;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.negative {
  color: #f56c6c;
}

.expand-body {
  padding: 4px 12px;
}

.expand-meta {
  margin-top: 8px;
}

.queue-title {
  color: #303133;
  font-weight: 600;
}

.queue-count {
  margin-left: 8px;
}

.queue-help {
  margin-bottom: 8px;
}

.raw-json {
  max-height: 320px;
  margin: 0;
  padding: 8px 12px;
  overflow: auto;
  background: #f5f7fa;
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
}

.cell-text {
  word-break: break-word;
}

.empty {
  padding: 16px;
  color: #909399;
  text-align: center;
}

.muted {
  color: #909399;
  font-size: 12px;
}

.mono {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  word-break: break-all;
}

.mono-input >>> input {
  font-family: Menlo, Consolas, monospace;
}
</style>
